import { DocumentCard } from '@dealstack/frontend/shared/components/DocumentCard/DocumentCard';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';

import { PreviewDocument } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/types';
import { ActionPanel } from '@dealstack/frontend/investor/pages/Valuations/ActionPanel';
import { useDealDocuments } from '@dealstack/frontend/investor/pages/Valuations/hooks/useDealDocuments';
import { useUserInvestmentDocuments } from '@dealstack/frontend/investor/pages/Valuations/hooks/useInvestorDocuments';
import { usePreviewDocument } from '@dealstack/frontend/investor/pages/Valuations/hooks/usePreviewDocument';
import { InvestorHoldingsTable } from '@dealstack/frontend/investor/pages/Valuations/InvestorHoldingsTable/InvestorHoldingsTable';
import { useHoldingsTableController } from '@dealstack/frontend/investor/pages/Valuations/useHoldingsTableController';
import {
	VALUATIONS_DRAWER_TYPE,
	useValuationsController,
} from '@dealstack/frontend/investor/pages/Valuations/useValuationsController';
import { InvestorDocumentsDrawer } from '@dealstack/frontend/investor/pages/Valuations/ValuationsDrawer/InvestorDocumentsDrawer';
import { ProgrammeContact } from '@dealstack/frontend/investor/pages/Valuations/ValuationsDrawer/ProgrammeContact';
import { DocumentViewerModalV2 } from '@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerModalV2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { TermsAndPolicy } from '@dealstack/frontend/shared/components/TermsAndPolicy/TermsAndPolicy';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useDownloadFileUsingFileStorageUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadFileUsingFileStorageUrl';
import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { getValidFileNameForDownload } from '@dealstack/shared/lib/common/file/file';
import { DocumentWorkflowInstanceStatus } from '@dealstack/shared/lib/generated';
import { resolveFileExtension } from '@dealstack/frontend/investor/pages/Valuations/Valuations.utils';
import { Box, Button, Flex, Grid, Text } from '@mantine/core';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import type { DocumentStatusUIDetails } from './Valuations.types';

export enum DOCUMENT_STATUS {
	SIGNED = 'signed',
	REJECTED = 'rejected',
	EXPIRED = 'expired',
	COMPLETED = 'completed',
}

export const Valuations = () => {
	const tInvestor = useTranslationInvestor();

	const {
		companyName,
		currentDrawer,
		setCurrentDrawer,
		programmeContactPersons,
		onCloseClicked,
		hasAllInvestmentsOption,
		documentStatusData,
		redirectToTransactionLists,
	} = useValuationsController();

	const { investorId } = useParams();
	const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null);
	const { handlePreviewFile } = usePreviewDocument({ setPreviewDocument });
	const { downloadFile } = useDownloadFileUsingFileStorageUrl();
	const { documents: userInvestmentDocuments, isLoading: isUserInvestmentDocumentsLoading } =
		useUserInvestmentDocuments({
			enabled: true,
			investorId: hasAllInvestmentsOption ? undefined : investorId,
		});

	const { value: isDealDocumentsEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.INVESTOR_PORTAL_DEAL_DOCUMENTS
	);

	const { documents: dealDocuments, isLoading: isDealDocumentsLoading } = useDealDocuments({
		enabled: isDealDocumentsEnabled && hasAllInvestmentsOption,
	});

	const newDocumentStatusData: Partial<
		Record<DocumentWorkflowInstanceStatus, DocumentStatusUIDetails>
	> = {
		[DocumentWorkflowInstanceStatus.COMPLETED]: documentStatusData[DOCUMENT_STATUS.COMPLETED],
		[DocumentWorkflowInstanceStatus.CANCELLED]: documentStatusData[DOCUMENT_STATUS.REJECTED],
		[DocumentWorkflowInstanceStatus.EXPIRED]: documentStatusData[DOCUMENT_STATUS.EXPIRED],
		[DocumentWorkflowInstanceStatus.ERROR]: documentStatusData[DOCUMENT_STATUS.EXPIRED],
	};

	const holdingsTableController = useHoldingsTableController(hasAllInvestmentsOption);

	const { holdingsData, showValuationsAlert } = holdingsTableController;

	const alertElement = showValuationsAlert
		? {
				title: tInvestor('valuations.note_valuation_title'),
				desc: tInvestor('valuations.note_valuation_text'),
			}
		: {
				title: tInvestor('valuations.no_holdings_title'),
				desc: tInvestor('valuations.no_holdings_text'),
			};

	const HoldingTableAlert = (
		<InfoBox
			title={alertElement.title}
			description={alertElement.desc}
			iconVariant="warning"
			alignContent="center"
		/>
	);

	return (
		<>
			<Box p="2rem">
				{/* v7 Grid's flex container is the `inner` part, so `gap` on the root
				    (the old sx) is inert; apply the row spacing to `inner` instead. */}
				<Grid w="100%" m={0} gutter={0} styles={{ inner: { rowGap: '2rem' } }}>
					{!hasAllInvestmentsOption && (
						<Grid.Col span={10} style={{ padding: 0 }}>
							<Text size="B3Bold">{companyName}</Text>
							<Button
								variant="transparent"
								onClick={() => {
									setCurrentDrawer(VALUATIONS_DRAWER_TYPE.PROGRAMME_CONTACTS);
								}}
								style={{ padding: 0 }}
							>
								<Text size="B6Bold" c={defaultTheme.v3.colors.blue[6]} mr="0.5rem">
									{tInvestor('valuations.programme_contacts_button', {
										programmeContactsNumber: programmeContactPersons.length,
									})}
								</Text>
							</Button>
						</Grid.Col>
					)}
					<ActionPanel hasAllInvestmentsOption={hasAllInvestmentsOption} />
					<Grid.Col span={12} style={{ padding: 0 }}>
						<Flex justify="space-between" align="center">
							<Text size="H5Bold">{tInvestor('valuations.holdings')}</Text>
							<Button
								rightSection={
									<Icon
										variant="arrow-right-v3"
										size="xl"
										color={defaultTheme.v3.colors.blue[6]}
									/>
								}
								variant="subtle"
								onClick={redirectToTransactionLists}
								style={{ display: 'flex', gap: '0.5rem' }}
							>
								{tInvestor('valuations.view_transactions')}
							</Button>
						</Flex>

						{holdingsTableController.isLoading ? (
							<InlineLoader />
						) : (
							<Flex direction="column" mt="0.5rem" gap="1.5rem">
								{(!holdingsData.length || showValuationsAlert) && HoldingTableAlert}
								{holdingsData.length ? (
									<InvestorHoldingsTable
										data={holdingsData}
										showAllHoldingsMode={hasAllInvestmentsOption}
									/>
								) : null}
							</Flex>
						)}
					</Grid.Col>

					<Grid.Col span={12} style={{ padding: '0 0 4rem 0' }}>
						<Box style={{ display: 'flex', justifyContent: 'space-between' }}>
							<Text size="H5Bold">
								{tInvestor('valuations.documents_section.title', {
									numberOfDocuments:
										(userInvestmentDocuments?.length ?? 0) +
										dealDocuments.length,
								})}
							</Text>
						</Box>

						{isUserInvestmentDocumentsLoading || isDealDocumentsLoading ? (
							<InlineLoader />
						) : (
							<Box
								style={{
									display: 'flex',
									gap: '1rem',
									flexWrap: 'wrap',
									marginTop: '0.5rem',
								}}
							>
								{userInvestmentDocuments?.map((document) => {
									const statusDetails = newDocumentStatusData[document.status];
									const fileExt = resolveFileExtension(document.name);
									return (
										<ValuationsDocumentCard
											key={document.id}
											documentID={document.id}
											documentTitle={document.name}
											fileExtension={fileExt}
											date={document.createdDate}
											valuationsDateFontSizeVariant={true}
											onClickCard={() => {
												handlePreviewFile({
													fileId: document.fileId,
													fileName: document.name,
													dealId: document.dealId,
													documentWorkflowInstanceId: document.id,
												});
											}}
											disabled={
												document.status ===
													DocumentWorkflowInstanceStatus.EXPIRED ||
												document.status ===
													DocumentWorkflowInstanceStatus.CANCELLED
											}
										>
											<DocumentCard.DropdownMenu>
												<DropdownField
													onClick={() => {
														void downloadFile({
															fileId: document.fileId,
															dealId: document.dealId,
															fileName: getValidFileNameForDownload(
																document.name,
																fileExt ?? FileExtension.pdf
															),
														});
													}}
												>
													<MenuItemText variant="sans/11/400">
														{tInvestor(
															'valuations.documents_section.download'
														)}
													</MenuItemText>
												</DropdownField>
											</DocumentCard.DropdownMenu>
											{statusDetails && (
												<DocumentCard.StatusChip
													variant="outline"
													style={{
														padding: '0.25rem 0.5rem',
														color: statusDetails.color,
														border: `1px solid ${statusDetails.color}`,
														borderRadius: '1rem',
														fontWeight: 400,
													}}
												>
													{statusDetails.text}
												</DocumentCard.StatusChip>
											)}
										</ValuationsDocumentCard>
									);
								})}

								{dealDocuments.map((document) => {
									const fileExt = resolveFileExtension(document.name);
									return (
										<ValuationsDocumentCard
											key={document.id}
											documentID={document.id}
											documentTitle={document.name}
											fileExtension={fileExt}
											date={document.createdDate}
											valuationsDateFontSizeVariant={true}
											onClickCard={() => {
												handlePreviewFile({
													fileId: document.fileId,
													fileName: document.name,
													dealId: document.dealId,
												});
											}}
										>
											<DocumentCard.DropdownMenu>
												<DropdownField
													onClick={() => {
														void downloadFile({
															fileId: document.fileId,
															dealId: document.dealId,
															fileName: getValidFileNameForDownload(
																document.name,
																fileExt ?? FileExtension.pdf
															),
														});
													}}
												>
													<MenuItemText variant="sans/11/400">
														{tInvestor(
															'valuations.documents_section.download'
														)}
													</MenuItemText>
												</DropdownField>
											</DocumentCard.DropdownMenu>
										</ValuationsDocumentCard>
									);
								})}
							</Box>
						)}
						{previewDocument && (
							<DocumentViewerModalV2
								opened
								file={previewDocument.fileBlob}
								fileName={previewDocument.fileName}
								fileExtension={previewDocument.fileExtension}
								isLoading={false}
								onClose={() => setPreviewDocument(null)}
							/>
						)}
					</Grid.Col>
				</Grid>

				<InvestorDocumentsDrawer
					onClose={onCloseClicked}
					titleText={tInvestor('valuations.drawer.programme_contact.title', {
						programmeContactsNumber: programmeContactPersons.length,
					})}
					opened={currentDrawer === VALUATIONS_DRAWER_TYPE.PROGRAMME_CONTACTS}
				>
					<ProgrammeContact persons={programmeContactPersons} />
				</InvestorDocumentsDrawer>
			</Box>
			<WrapperTermAndPolicy>
				<TermsAndPolicy isInvestorApp />
			</WrapperTermAndPolicy>
		</>
	);
};

const DropdownField = styled.div`
	display: contents;
	cursor: pointer;
`;

const MenuItemText = styled(TextV2)`
	color: ${({ theme }) => theme.v2.color.neutral[700]};
	padding: 0.25em;
`;

const ValuationsDocumentCard = styled(DocumentCard)`
	width: 17rem;
	${({ disabled }) =>
		disabled &&
		css`
			opacity: 1;
			pointer-events: none;
		`}
`;

const WrapperTermAndPolicy = styled.div`
	margin-top: auto;
	padding-bottom: 2rem;
`;
